/**
 * iter844 — Printer troubleshooting help drawer.
 *
 * Shown from the "Help" button on Printers Settings. Answers the top 5
 * "why isn't my printer working?" questions users ask, tailored to
 * their current device (Android app / iOS / desktop / Windows browser).
 * Deliberately compact — cashiers should be able to self-serve without
 * a support call.
 */
import { useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { HelpCircle, Usb, Wifi, Smartphone, CheckCircle2, RefreshCw, Shield } from 'lucide-react';
import { isCapacitorNative, isAndroid } from '@/lib/platformDetect';
import { isLikelyIOS } from '@/lib/printBridge';

const Row = ({ icon: Icon, title, children, tone = 'slate' }) => {
  const cls = {
    green: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 text-emerald-900 dark:text-emerald-100',
    amber: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100',
    sky: 'bg-sky-50 dark:bg-sky-950/30 border-sky-200 dark:border-sky-800 text-sky-900 dark:text-sky-100',
    slate: 'bg-slate-50 dark:bg-slate-950/40 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100',
  }[tone];
  return (
    <div className={`rounded-lg border p-3 ${cls}`}>
      <div className="flex items-start gap-2">
        <Icon size={16} className="mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">{title}</div>
          <div className="text-[12px] mt-1 leading-relaxed opacity-90">{children}</div>
        </div>
      </div>
    </div>
  );
};

export default function PrinterHelpDrawer({ open, onOpenChange }) {
  const context = useMemo(() => {
    if (isCapacitorNative() && isAndroid()) return 'android_native';
    if (isAndroid()) return 'android_browser';
    if (isLikelyIOS()) return 'ipad';
    return 'desktop';
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85dvh] overflow-y-auto" data-testid="printer-help-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle size={20} className="text-orange-500" />
            Printer isn&apos;t working — quick fixes
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-2 text-sm">

          {context === 'android_browser' && (
            <Row icon={Smartphone} title="You&apos;re in the browser — USB won&apos;t work here" tone="amber">
              Android browsers cannot access USB or Bluetooth printers. Install the <b>Smarto Android app</b> from the download button above, log in there, and USB will start working.
            </Row>
          )}

          {context === 'android_native' && (
            <>
              <Row icon={CheckCircle2} title="You're in the Smarto Android app ✓" tone="green">
                Great — USB / Bluetooth printers can work here. But you must add the printer <b>inside this app</b> first — an OS-level &quot;Default Print Service&quot; is <b>not</b> supported for thermal printers.
              </Row>

              <Row icon={Shield} title="⚠ &quot;Sent but nothing prints&quot;? Read this first" tone="amber">
                <b>Most common cause</b>: the printer&apos;s <b>Connection method</b> in Settings → Printers is left on <b>Network</b> (default) with no IP address. The app then falls back to Android&apos;s OS Print Service, which cannot see thermal receipt printers. <b>Fix</b>: edit the printer, set <b>Connection = USB</b>, tap <b>Scan</b>, pick your device, Save. Then Test Print — you should hear the printer&apos;s motor within 1 second.
              </Row>

              <Row icon={Usb} title="Step 1 · Plug printer via USB-OTG cable" tone="sky">
                Use a USB-OTG (USB-C or micro-USB to USB-A female) adapter. When you plug in the printer, Android shows <b>&quot;Allow Smarto to access this USB device?&quot;</b>. <b>Tap Allow</b> and tick <b>Use by default for this USB device</b>. Without this permission the printer is invisible and prints will silently fail.
              </Row>

              <Row icon={RefreshCw} title="Step 2 · Add printer with connection = USB" tone="sky">
                Go to <b>Settings → Printers → Add printer</b>. Set <b>Connection method = USB</b> then tap <b>Scan</b>. Your printer should appear (e.g., <i>USB\VID_0416&amp;PID_5011</i>). Tap it to auto-fill the device id, name it (e.g., <i>Receipt · Front</i>), select paper width (58mm or 80mm), and <b>Save</b>.
              </Row>

              <Row icon={Shield} title="Step 3 · Test Print (top-right of the printer row)" tone="sky">
                Test Print should feed paper immediately.<br />
                • <b>Nothing at all</b> → USB permission not granted. Unplug, replug, tap Allow.<br />
                • <b>Toast: &quot;Device not found&quot;</b> → device_address is stale. Delete and re-add via Scan.<br />
                • <b>Toast: &quot;Native USB print failed&quot;</b> → wrong VID/PID or non-ESC/POS printer. Check the printer&apos;s specs.
              </Row>

              <Row icon={Shield} title="Step 4 · If Android opens &quot;Default Print Service&quot; screen — WRONG PATH" tone="amber">
                If tapping Print opens the OS Android Print Service (a page listing Wi-Fi printers, showing &quot;1. The printer isn&apos;t on the same Wi-Fi&quot;) — that means the printer&apos;s <b>Connection method</b> is still Network. Go back to Settings → Printers, edit the printer, and switch to <b>USB</b>. Thermal receipt printers never appear in Android&apos;s Default Print Service.
              </Row>

              <Row icon={RefreshCw} title="Still not working? Update your APK" tone="amber">
                Your APK might be older than iter845 and be missing the SmartoPrinter Capacitor plugin. Uninstall the current Smarto app, then download the latest APK from <a href="/api/apk/download" className="underline font-semibold">/api/apk/download</a> and reinstall.
              </Row>
            </>
          )}

          {context === 'ipad' && (
            <>
              <Row icon={Wifi} title="iPad = AirPrint or Bridge only" tone="amber">
                iPad cannot use USB directly. Two paths: (a) Enable <b>AirPrint</b> on your printer via router, mark the printer &quot;AirPrint enabled&quot; here. (b) Run the <b>Smarto Print Bridge</b> on a nearby Windows/Mac PC in the same Wi-Fi; iPad relays through the bridge.
              </Row>
            </>
          )}

          {context === 'desktop' && (
            <>
              <Row icon={Usb} title="Windows / Mac desktop path" tone="sky">
                Best experience: install the <b>Smarto Desktop app</b> (download from the landing page → &quot;Windows&quot; button). It has direct USB access without needing the LAN Bridge. Add printer with <b>Connection = USB</b> and use the <b>Detect from Desktop</b> button to auto-fill VID / PID.
              </Row>
              <Row icon={Wifi} title="Alternative: LAN thermal printer via Print Bridge" tone="sky">
                Plug your USB printer into a PC, run the Print Bridge tray app on that PC, and every device on the same Wi-Fi (iPads, tablets, other PCs) can print through it.
              </Row>
            </>
          )}

          <Row icon={CheckCircle2} title="Still stuck? Send us a screenshot" tone="slate">
            Take a screenshot of your Printer Settings page + the exact error toast from Test Print. Include your printer make/model (e.g., <i>Epson TM-T88VI</i>). The support team can pinpoint the fix in 5 minutes.
          </Row>

        </div>
      </DialogContent>
    </Dialog>
  );
}
